import { useEffect } from 'react';
import Button from '@/components/Button';
import CurrencySelectBox, { CurrencyItem } from '@/components/CurrencySelectBox';
import DatePicker from '@/components/datepicker/DatePicker';
import { toDateValue, toTimeValue } from '@/components/datepicker/mappers';
import TimeField from '@/components/datepicker/TimeField';
import NumberInput from '@/components/NumberInput';
import PriceInput from '@/components/PriceInput';
import RetailerSelectBox from '@/components/RetailerSelectBox';
import StoreSelectBox from '@/components/StoreSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import { useSettings } from '@/contexts/SettingsContext';
import {
  Discount,
  DiscountBxgy,
  DiscountFixedAmount,
  DiscountPercentage,
  DiscountType,
  DiscountTypeLabels,
  discountTypes,
  getDiscountType,
  isBxgyDiscount,
  isFixedAmountDiscount,
  isPercentageDiscount,
} from '@/models/Discount';
import { Promotion, PromotionCreate, PromotionUpdate } from '@/models/Promotion';
import { Retailer } from '@/models/Retailer';
import { Store } from '@/models/Store';
import PromotionProductForm from '@/modules/product/components/PromotionProductForm';
import { CheckIcon } from '@heroicons/react/24/outline';
import { DateValue } from '@react-aria/calendar';
import { TimeValue } from '@react-aria/datepicker';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface Sku {
  id: string;
  name: string;
  externalCode?: string;
}

interface PromotionFormProps {
  onSubmit: (promotion: PromotionCreate | PromotionUpdate) => void;
  promotion?: PromotionCreate | Promotion;
  skus?: Sku[];
  currencyCode?: string;
  timezone?: string;
  // supplier?: Supplier | null;
  supplierId?: string;
  // showSupplier?: boolean;
  // showSupplierSelect?: boolean;
}

interface FormData extends PromotionCreate, PromotionUpdate {
  startDate?: DateValue | null;
  startTime?: TimeValue | null;
  endDate?: DateValue | null;
  endTime?: TimeValue | null;
  discountType: DiscountType;
  skus: Sku[];
}

/**
 * Returns a new Date set to the beginning of the month for the given date.
 */
function startOfMonth(date: Date): Date {
  const start = new Date(date);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Returns a new Date set to the end of the month, N months after the given date.
 * @param date - the reference date
 * @param monthsAhead - number of months ahead
 */
function endOfMonthAfterMonths(date: Date, monthsAhead: number): Date {
  const end = new Date(date);
  end.setMonth(end.getMonth() + monthsAhead + 1); // move to next month
  end.setDate(0); // last day of target month
  end.setHours(23, 59, 59, 999);
  return end;
}

const PromotionForm = ({
  onSubmit,
  promotion,
  skus: skusProp = [],
  currencyCode: currencyCodeProp,
  // supplier,
  supplierId,
  // showSupplier,
  // showSupplierSelect,
}: PromotionFormProps) => {
  const getDefaultDiscounts = (currencyCode: string): Record<DiscountType, Discount> => ({
    fixedAmount: {
      discountType: 'fixedAmount',
      amount: {
        currencyCode,
        amount: 1,
      },
    },
    percentage: {
      discountType: 'percentage',
      percentage: 10,
    },
    bxgy: {
      discountType: 'bxgy',
      buyQuantity: 6,
      getQuantity: 1,
    },
  });

  const { timezone, locale } = useSettings();

  const defaultDiscounts = getDefaultDiscounts(currencyCodeProp || '');

  const defaultStart = startOfMonth(new Date());
  const defaultEnd = endOfMonthAfterMonths(defaultStart, 6);

  const methods = useForm<FormData>({
    defaultValues: promotion
      ? {
          ...promotion,
          startDate: toDateValue(promotion.effectivePeriod?.start, timezone),
          startTime: toTimeValue(promotion.effectivePeriod?.start, timezone),
          endDate: toDateValue(promotion.effectivePeriod?.end, timezone),
          endTime: toTimeValue(promotion.effectivePeriod?.end, timezone),
          discountType: getDiscountType(promotion.discounts) || 'fixedAmount',
          discounts:
            promotion.discounts.length > 0
              ? promotion.discounts
              : [defaultDiscounts['fixedAmount']],
          skus: skusProp,
        }
      : {
          id: '',
          name: '',
          status: 'ACTIVE',
          supplierId: supplierId || '',
          discounts: [defaultDiscounts['fixedAmount']],
          minimumAmount: undefined,
          minimumQuantity: 0,
          effectivePeriod: {
            start: defaultStart,
            end: defaultEnd,
          },
          startDate: toDateValue(defaultStart, timezone),
          startTime: toTimeValue(defaultStart, timezone),
          endDate: toDateValue(defaultEnd, timezone),
          endTime: toTimeValue(defaultEnd, timezone),
          currencyCode: currencyCodeProp || '',
          discountType: 'fixedAmount',
          productCategoryId: undefined,
          productListId: undefined,
          skus: skusProp,
          skuIds: skusProp.map((s) => s.id),
          customerIds: [],
          storeIds: [],
        },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = methods;

  const formValues = watch();

  const handleStatusChange = (checked: boolean) =>
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  const handleCurrencyChange = (currency: CurrencyItem | null) =>
    setValue('currencyCode', currency?.id || '');
  const handleDiscountTypeChange = (type: DiscountType) => {
    setValue('discountType', type);
    setValue('discounts', [defaultDiscounts[type]], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  const handleFixedAmountChange = (amount: number) => {
    if (!formValues.currencyCode) return;

    const updatedDiscount: DiscountFixedAmount = {
      discountType: 'fixedAmount',
      amount: {
        currencyCode: formValues.currencyCode,
        amount,
      },
    };

    setValue('discounts', [updatedDiscount], { shouldValidate: true, shouldDirty: true });
  };

  const handlePercentageChange = (percentage: number) => {
    const updatedDiscount: DiscountPercentage = {
      discountType: 'percentage',
      percentage,
    };

    setValue('discounts', [updatedDiscount], { shouldValidate: true, shouldDirty: true });
  };

  const handleMinimumAmountChange = (amount: number) => {
    if (!formValues.currencyCode) return;
    setValue('minimumAmount', { amount, currencyCode: formValues.currencyCode });
  };

  const handleMinimumQuantityChange = (quantity: number) => setValue('minimumQuantity', quantity);

  const handleCustomerChange = (customers: Retailer[] | null) =>
    setValue(
      'customerIds',
      customers?.map((c) => c.id),
    );

  const handleStoreChange = (stores: Store[] | null) =>
    setValue(
      'storeIds',
      stores?.map((s) => s.id),
    );

  const updateEffectivePeriod = () => {
    const startDate = getValues('startDate');
    const startTime = getValues('startTime');
    const endDate = getValues('endDate');
    const endTime = getValues('endTime');

    if (startDate && startTime && endDate && endTime) {
      const start = new Date(
        startDate.year,
        startDate.month - 1,
        startDate.day,
        startTime.hour,
        startTime.minute,
      );
      const end = new Date(
        endDate.year,
        endDate.month - 1,
        endDate.day,
        endTime.hour,
        endTime.minute,
      );

      setValue('effectivePeriod', { start, end }, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleStartDateChange = (date: DateValue | null) => {
    setValue('startDate', date, { shouldValidate: true, shouldDirty: true });
    updateEffectivePeriod();
  };

  const handleEndDateChange = (date: DateValue | null) => {
    setValue('endDate', date, { shouldValidate: true, shouldDirty: true });
    updateEffectivePeriod();
  };

  const handleStartTimeChange = (time: TimeValue | null) => {
    setValue('startTime', time, { shouldValidate: true, shouldDirty: true });
    updateEffectivePeriod();
  };

  const handleEndTimeChange = (time: TimeValue | null) => {
    setValue('endTime', time, { shouldValidate: true, shouldDirty: true });
    updateEffectivePeriod();
  };

  const handleBxgyChange = (field: 'buyQuantity' | 'getQuantity', value: number) => {
    const currentDiscounts = getValues('discounts') || [];
    const existingBxgy = currentDiscounts.find(isBxgyDiscount);

    const updatedDiscount: DiscountBxgy = existingBxgy
      ? { ...existingBxgy, [field]: value }
      : {
          discountType: 'bxgy',
          buyQuantity: field === 'buyQuantity' ? value : 0,
          getQuantity: field === 'getQuantity' ? value : 0,
        };

    setValue('discounts', [updatedDiscount], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    register('startDate', {
      required: 'Start date is required',
    });
    register('startTime', {
      required: 'Start time is required',
    });
    register('endDate', {
      required: 'End date is required',
    });
    register('endTime', {
      required: 'End time is required',
    });
  }, [register]);

  const fixedAmountDiscount = formValues.discounts.find(isFixedAmountDiscount);
  const percentageDiscount = formValues.discounts.find(isPercentageDiscount);
  const bxgyDiscount = formValues.discounts.find(isBxgyDiscount);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4 lg:gap-8">
          {/* Name */}
          <div className="col-span-2">
            <label htmlFor="name" className="block font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              {...register('name', { required: 'Name is required' })}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          {/* Currency */}
          {!currencyCodeProp && (
            <div className="col-span-1">
              <Controller
                control={control}
                name="currencyCode"
                render={({ field }) => (
                  <CurrencySelectBox
                    value={field.value}
                    onSelect={(currency) => {
                      field.onChange(currency?.id || '');
                      handleCurrencyChange(currency);
                    }}
                    requireSelection
                  />
                )}
              />
            </div>
          )}

          <div className="col-span-2">
            <span className="block font-bold text-xl mb-6 pb-4 border-b border-gray-300">
              Discount <span className="text-red-500">*</span>
            </span>
            <div className="flex gap-4">
              {discountTypes.map((type) => (
                <div
                  key={type}
                  className="flex flex-1 items-center ps-4 border border-gray-200 rounded"
                >
                  <input
                    type="radio"
                    name="discountType"
                    checked={formValues.discountType === type}
                    onChange={() => handleDiscountTypeChange(type)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="w-full py-4 ms-2 text-sm font-medium text-gray-900">
                    {DiscountTypeLabels[type]}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed Amount */}
          {formValues.discountType === 'fixedAmount' && (
            <div className="col-span-2 md:col-span-1">
              <label className="block font-medium text-gray-700 mb-1">Fixed Amount:</label>
              <PriceInput
                value={fixedAmountDiscount?.amount.amount || ''}
                currencyCode={formValues.currencyCode || ''}
                onChange={handleFixedAmountChange}
              />
            </div>
          )}

          {/* Percentage */}
          {formValues.discountType === 'percentage' && (
            <div className="col-span-2 md:col-span-1">
              <label className="block font-medium text-gray-700 mb-1">Percentage:</label>
              <NumberInput
                value={percentageDiscount?.percentage || 0}
                onChange={handlePercentageChange}
                className="w-full"
              />
            </div>
          )}

          {/* Buy X Get Y */}
          {formValues.discountType === 'bxgy' && (
            <div className="col-span-2 md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Buy Quantity:</label>
                <NumberInput
                  value={bxgyDiscount?.buyQuantity || 1}
                  onChange={(value) => handleBxgyChange('buyQuantity', value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Get Quantity:</label>
                <NumberInput
                  value={bxgyDiscount?.getQuantity || 1}
                  onChange={(value) => handleBxgyChange('getQuantity', value)}
                  className="w-full"
                />
              </div>
            </div>
          )}

          <div className="col-span-2 md:col-span-2 grid grid-cols-2 gap-4">
            {/* Minimum Amount */}
            <div className="col-span-2 md:col-span-1">
              <label className="block font-medium text-gray-700 mb-1">Minimum Amount:</label>
              <PriceInput
                value={formValues.minimumAmount?.amount || ''}
                currencyCode={formValues.currencyCode || ''}
                onChange={handleMinimumAmountChange}
              />
            </div>

            {/* Minimum Quantity */}
            <div className="col-span-2 md:col-span-1">
              <label className="block font-medium text-gray-700 mb-1">Minimum Quantity:</label>
              <NumberInput
                value={formValues.minimumQuantity || 0}
                onChange={handleMinimumQuantityChange}
                className="w-full"
              />
            </div>
          </div>

          {/* Products */}
          <div className="col-span-2 my-4">
            <PromotionProductForm supplierId={supplierId} />
          </div>

          {/* Effective Period */}
          <div className="col-span-2 mb-4">
            <span className="block font-bold text-xl mb-6 pb-4 border-b border-gray-300">
              Effective Period <span className="text-red-500">*</span>
            </span>
            <div className="md:grid md:grid-cols-2 flex flex-col gap-4 lg:gap-8">
              <div className="flex flex-row items-end colspan-1 gap-4">
                <div className="flex-1">
                  <DatePicker
                    label="Start:"
                    value={formValues.startDate}
                    onChange={handleStartDateChange}
                    locale={locale}
                  />
                  {errors.startDate && (
                    <span className="text-red-500 text-sm mt-1">{errors.startDate.message}</span>
                  )}
                </div>
                <div className="flex-1">
                  <TimeField value={formValues.startTime} onChange={handleStartTimeChange} />
                  {errors.startTime && (
                    <span className="text-red-500 text-sm mt-1">{errors.startTime.message}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-row items-end colspan-1 gap-4">
                <div className="flex-1">
                  <DatePicker
                    label="End:"
                    value={formValues.endDate}
                    onChange={handleEndDateChange}
                    locale={locale}
                  />
                  {errors.endDate && (
                    <span className="text-red-500 text-sm mt-1">{errors.endDate.message}</span>
                  )}
                </div>
                <div className="flex-1">
                  <TimeField value={formValues.endTime} onChange={handleEndTimeChange} />
                  {errors.endTime && (
                    <span className="text-red-500 text-sm mt-1">{errors.endTime.message}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Target */}
          <div className="col-span-2 mb-4">
            <span className="block font-bold text-xl mb-6 pb-4 border-b border-gray-300">
              Target
            </span>
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-8">
              <div className="flex-1 w-1/2">
                <RetailerSelectBox
                  value={formValues.customerIds || []}
                  onSelect={handleCustomerChange}
                  deselectLabel="Include all retailers"
                  multiple
                />
              </div>
              <div className="flex-1">
                <StoreSelectBox
                  value={formValues.storeIds || []}
                  onSelect={handleStoreChange}
                  retailerId={formValues.customerIds}
                  deselectLabel="Include all stores"
                  multiple
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="col-span-2">
            <ToggleSwitch
              checked={formValues.status === 'ACTIVE'}
              onChange={handleStatusChange}
              labelText="Status:"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button type="submit" icon={<CheckIcon className="h-6 w-6" />} variant="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default PromotionForm;
